import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { DocFile } from 'src/app/modules/loan-docs/models/doc-file.model';
import { DocsService } from 'src/app/modules/loan-docs/services/docs.service';
import { ExportFiles } from '../../models/export-files.model';
import { ExportLoanDocFiles } from '../../models/export-loan-doc-files.model';
import { ExportLoanDocsFilter } from '../../models/export-loan-docs-filter.model';
import { openDownloadedFileForViewing, toApiDownloadFileName } from 'src/app/shared/utils/file-type-utils';

@Component({
  selector: 'export-loan-docs-accordion',
  templateUrl: 'export-loan-docs-accordion.component.html',
  styleUrls: ['export-loan-docs-accordion.component.scss'],
  standalone: false
})

export class ExportLoanDocsAccordionComponent implements OnChanges {

  @Input()
  set files({ docFiles, documentTypeId, documentTypeName }: ExportFiles) {
    this.filteredFiles = [...docFiles];
    this._filteredFiles = [...this.filteredFiles];
    this.documentTypeId = `${documentTypeId}`;
    this.documentTypeName = documentTypeName;
  }

  @Input()
  set isSelectAllClicked(isClicked: boolean) {
    this.setRowCheckBoxStatus(isClicked);
  }

  @Input()
  set filter(filter: ExportLoanDocsFilter) {
    this.doFilter(filter);
  }

  @Input() allExpanded: boolean = false;

  documentTypeId: String;

  documentTypeName: String;

  filteredFiles: ExportLoanDocFiles[] = [];

  selectedRows: any[];

  dateFormat: string = 'MM/dd/yyyy h:mma';

  @Output()
  filesUpdated: EventEmitter<ExportLoanDocFiles> = new EventEmitter<ExportLoanDocFiles>();

  @Output()
  filesArrayUpdated: EventEmitter<ExportLoanDocFiles[]> = new EventEmitter<ExportLoanDocFiles[]>();

  private _filteredFiles: ExportLoanDocFiles[] = [];

  constructor(private readonly _docsService: DocsService,
    private readonly _spinner: GlobalSpinnerService,
  ) { }

  ngOnChanges(_: SimpleChanges): void {
    this.selectedRows = this.filteredFiles.filter(({ checked }) => checked);
  }

  onToggleCheckboxClicked = (file: ExportLoanDocFiles) => {
    const fileIndex = this.filteredFiles.findIndex(f => f.guid === file.guid);
    if (fileIndex !== -1) {
      const predictedCheckedState = !this.filteredFiles[fileIndex].checked;
      this.filesUpdated.emit({ ...file, checked: predictedCheckedState });

      setTimeout(() => {
        const isInSelectedRows = this.selectedRows.some(f => f.guid === file.guid);
        this.filteredFiles[fileIndex].checked = isInSelectedRows;

        if (isInSelectedRows !== predictedCheckedState) {
          this.filesUpdated.emit({ ...this.filteredFiles[fileIndex] });
        }
      }, 0);
    }
  }

  onSelectAllByDocumentClicked = () => {
    const predictedCheckedState = !this.filteredFiles.every(f => f.checked);
    this.filteredFiles.forEach(file => {
      file.checked = predictedCheckedState;
    });
    this.filesArrayUpdated.emit([...this.filteredFiles]);

    setTimeout(() => {
      this.filteredFiles.forEach(file => {
        file.checked = this.selectedRows.some(f => f.guid === file.guid);
      });
      this.filesArrayUpdated.emit([...this.filteredFiles]);
    }, 0);
  }

  onViewDocFileClicked = (file: DocFile) => {
    this._spinner.show();
    this._docsService.viewFileWithName(file.guid).subscribe((downloaded) => {
      this._spinner.hide();
      openDownloadedFileForViewing(downloaded, file.fileName, file.mimeType);
    }, error => {
      this._spinner.hide();
    })
  }

  onDownloadDocumentClicked = (file: DocFile) => {
    this._spinner.show();
    this._docsService.downloadFileWithName(file.guid).subscribe(downloaded => {
      this._spinner.hide();
      const blob = new Blob([downloaded.body]);
      let downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      const fileName = toApiDownloadFileName(downloaded.fileName, file.fileName, file.mimeType);
      downloadLink.setAttribute('download', fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
    }, error => {
      this._spinner.hide();
    })
  }

  private doFilter = (filter: ExportLoanDocsFilter) => {
    if (filter.filter === 'all') {
      this.filteredFiles = this._filteredFiles.filter(file => (!filter.excludeNa || file.taskStatus != 'NotApplicable') && (!filter.excludeCleared || (file.conditionStatus != 'Cleared' && file.conditionStatus != 'ConditionCleared')));
    }
    else if (filter.filter === 'task') {
      this.filteredFiles = this._filteredFiles.filter(file => !file.condition && file.taskStatus && (!filter.excludeNa || file.taskStatus != 'NotApplicable'));
    } else {
      this.filteredFiles = this._filteredFiles.filter(file => file.condition && (!filter.excludeCleared || (file.conditionStatus != 'Cleared' && file.conditionStatus != 'ConditionCleared')) && (!filter.excludeNa || file.taskStatus != 'NotApplicable'));
    }
  }

  private setRowCheckBoxStatus = (isChecked: boolean) => {
    if (this.filteredFiles.length > 0) {
      this.filteredFiles = this.filteredFiles.map((file) => ({ ...file, checked: isChecked }));
      this.selectedRows = isChecked ? [...this.filteredFiles] : [];
      this.filesArrayUpdated.emit(this.filteredFiles);
    }
  }
}
